import { RoleModuleConfig } from './types';
import { DXAReadingsSlice } from '@restore/DXAReadings/DXAReadings.slice';
import { QCTReadingsSlice } from '@restore/QCTReadings/QCTReadings.slice';
import { QCTaskCommentsSlice } from '@restore/QCTaskComments/QCTaskComments.slice';
import { QCTaskPatientSlice } from '@restore/QCTaskPatient/QCTaskPatient.slice';
import { QCTaskSeriesSlice } from '@restore/QCTaskSeries/QCTaskSeries.slice';
import { QCTasksListSlice } from '@restore/QCTasksList/QCTasksList.slice';
import { QCPatientVisitsDatesSlice } from '@restore/QCPatientVisitsDates/QCPatientVisitsDates.slice';
import { QCTaskUploadsListSlice } from '@restore/QCTaskUploadsList/QCTaskUploadsList.slice';
import { QCTaskSlice } from '@restore/QCTask/QCTask.slice';
import { EDTFSlice } from '@restore/EDTF/EDTF.slice';
import { DICOMViewerProcessesSlice } from '@restore/DICOMViewerProcesses/DICOMViewerProcesses.slice';
import { qcTaskDefinitionSlice } from '@restore/QCTaskDefinition/QCTaskDefinition.slice';
import { qcTaskExecutionsSlice } from '@restore/QCTaskExecutions/QCTaskExecutions.slice';
import { DXAReadingsSaga } from '@restore/DXAReadings/DXAReadings.saga';
import { QCTReadingsSaga } from '@restore/QCTReadings/QCTReadings.saga';
import { QCTaskCommentsSaga } from '@restore/QCTaskComments/QCTaskComments.saga';
import { QCTaskPatientSaga } from '@restore/QCTaskPatient/QCTaskPatient.saga';
import { QCTaskSeriesSaga } from '@restore/QCTaskSeries/QCTaskSeries.saga';
import { QCTasksListSaga } from '@restore/QCTasksList/QCTasksList.saga';
import { QCPatientVisitsDatesSaga } from '@restore/QCPatientVisitsDates/QCPatientVisitsDates.saga';
import { QCTaskUploadsListSaga } from '@restore/QCTaskUploadsList/QCTaskUploadsList.saga';
import { QCTaskSaga } from '@restore/QCTask/QCTask.saga';
import { EDTFSaga } from '@restore/EDTF/EDTF.saga';
import { DICOMViewerProcessesSaga } from '@restore/DICOMViewerProcesses/DICOMViewerProcesses.saga';
import { QCTaskDefinitionsSaga } from '@restore/QCTaskDefinition/QCTaskDefinition.saga';
import { QCTaskExecutionsSaga } from '@restore/QCTaskExecutions/QCTaskExecutions.saga';
import { DICOMViewerSlice } from '@restore/DICOMViewer/DICOMViewer.slice';
import { DICOMViewerSaga } from '@restore/DICOMViewer/DICOMViewer.saga';
import { clinicalTrialInventorySlice } from '@restore/clinicalTrialInventory/clinicalTrialInventory.slice';
import { clinicalTrialInventorySaga } from '@restore/clinicalTrialInventory/clinicalTrialInventory.saga';
import { qcQueriesSlice } from '../qcQueries/qcQueries.slice';
import { qcQueriesSaga } from '../qcQueries/qcQueries.saga';

/**
 * QC role modules — deliberately without segmentation (D7-7995)
 *
 * Plain terms
 * -----------
 * The segmentation module links the viewer to a reader's scoring form: it
 * forwards what the reader draws to the form and saves, locks or deletes the
 * matching measurement on the reading task. An Imaging Specialist has neither
 * a scoring form nor a reading task, so the module has nothing to do here —
 * and loading it anyway broke it on the first viewer event of every QC
 * session. Segmentation files an Imaging Specialist imports go through the
 * viewer-process flow (`DICOMViewerProcessesSaga`), not this module.
 *
 * Technical
 * ---------
 * Every `segmentationSaga` worker reads `selectStoreDataRelatedToMeasurements`,
 * which needs the `measurements` / `readerTask` / `readerSubTask` slices this
 * role never mounts. Viewer events reach the store for every role
 * (`services/rpc/viewer/listeners.ts`), so `VIEWER__BUSY_CHANGED` threw on
 * `state.activeMeasurement` and, the saga being unsupervised (`injectSaga`),
 * cancelled all its watchers. The viewer sends the segmentation lock /
 * unlock / delete requests only on scoring-form commands (or, for delete, for
 * an existing measurement), neither of which QC has, so these requests are
 * not expected here; one that did arrive would fall to the RPC timeout via
 * `services/rpc/viewer/handlers.ts`, as it effectively did before.
 */
export const imagingSpecialistModules: RoleModuleConfig = {
  slices: [
    EDTFSlice,
    DXAReadingsSlice,
    QCTReadingsSlice,
    QCTaskCommentsSlice,
    QCTaskPatientSlice,
    QCTaskSeriesSlice,
    QCTasksListSlice,
    QCPatientVisitsDatesSlice,
    QCTaskUploadsListSlice,
    QCTaskSlice,
    DICOMViewerSlice,
    DICOMViewerProcessesSlice,
    qcTaskDefinitionSlice,
    qcTaskExecutionsSlice,
    clinicalTrialInventorySlice,
    qcQueriesSlice,
  ],
  sagas: {
    clinicalTrialInventorySaga,
    DICOMViewerSaga,
    DXAReadingsSaga,
    QCTReadingsSaga,
    QCTaskCommentsSaga,
    QCTaskPatientSaga,
    QCTaskSeriesSaga,
    QCTasksListSaga,
    QCPatientVisitsDatesSaga,
    QCTaskUploadsListSaga,
    QCTaskSaga,
    EDTFSaga,
    DICOMViewerProcessesSaga,
    QCTaskDefinitionsSaga,
    QCTaskExecutionsSaga,
    qcQueriesSaga,
  },
};
